import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useAcademy } from '@/components/academy/AcademyProvider';
import { AcademyBlocks } from '@/components/academy/AcademyBlocks';
import { Button } from '@/components/ui/button';
import { Loader2, ArrowLeft, PlayCircle, ExternalLink, CheckCircle2, ChevronLeft, ChevronRight, Clock } from 'lucide-react';

export default function AcademyLessonPage() {
  const { lessonId } = useParams();
  const { content, completed, toggle, startTour } = useAcademy();
  const { isStaff } = useAuth();
  const navigate = useNavigate();

  if (!content) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;
  }

  const idx = content.lessons.findIndex(l => l.id === lessonId);
  const lesson = content.lessons[idx];
  if (!lesson) {
    return (
      <div className="p-10 text-center font-ibm text-slate-500" data-testid="academy-lesson-missing">
        Lektionen finns inte eller är inte tillgänglig för din roll. <Link to="/academy" className="text-red-600 underline">Till Academy</Link>
      </div>
    );
  }
  const prev = content.lessons[idx - 1];
  const next = content.lessons[idx + 1];
  const category = content.categories.find(c => c.id === lesson.category);
  const checkItem = content.checklist.find(i => i.lesson === lesson.id && !i.auto);
  const isDone = checkItem && completed.includes(checkItem.id);

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-10 py-6 md:py-10" data-testid="academy-lesson-page">
      <button onClick={() => navigate('/academy')} className="flex items-center gap-1.5 text-xs font-ibm text-slate-400 hover:text-navy mb-6" data-testid="academy-lesson-back">
        <ArrowLeft className="h-3.5 w-3.5" />Vapelarm Academy{category ? ` / ${category.title}` : ''}
      </button>
      <h1 className="font-chivo font-black text-3xl md:text-4xl text-navy dark:text-white tracking-tight" data-testid="academy-lesson-title">{lesson.title}</h1>
      <p className="text-slate-500 font-ibm mt-2 flex items-center gap-2 text-sm"><Clock className="h-3.5 w-3.5" />{lesson.minutes} min · {lesson.summary}</p>

      <div className="flex flex-wrap gap-2 mt-5">
        {lesson.tour && (
          <Button onClick={() => startTour(lesson.tour)} className="bg-red-600 hover:bg-red-700 gap-2" data-testid="academy-start-tour-btn">
            <PlayCircle className="h-4 w-4" />Starta interaktiv guide
          </Button>
        )}
        {lesson.route && !isStaff() && (
          <Button variant="outline" onClick={() => navigate(lesson.route)} className="gap-2" data-testid="academy-open-page-btn">
            <ExternalLink className="h-4 w-4" />Öppna sidan
          </Button>
        )}
      </div>

      <div className="mt-10 space-y-12">
        {lesson.sections.map(sid => {
          const sec = content.sections[sid];
          return (
            <section key={sid} data-testid={`academy-section-${sid}`}>
              <h2 className="font-chivo font-bold text-xl text-navy dark:text-white mb-4">{sec.title}</h2>
              <AcademyBlocks blocks={sec.blocks} support={content.support} />
            </section>
          );
        })}
      </div>

      {checkItem && (
        <div className="mt-12 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 flex flex-wrap items-center gap-4">
          <CheckCircle2 className={`h-6 w-6 ${isDone ? 'text-emerald-500' : 'text-slate-300'}`} />
          <p className="flex-1 font-ibm text-sm text-slate-700 dark:text-slate-200">{checkItem.label}</p>
          <Button variant={isDone ? 'outline' : 'default'} className={isDone ? '' : 'bg-navy hover:bg-navy/90'} onClick={() => toggle(checkItem.id, !isDone)} data-testid="academy-lesson-complete-btn">
            {isDone ? 'Klar – ångra' : 'Markera som klar'}
          </Button>
        </div>
      )}

      <nav className="mt-10 flex justify-between gap-4 border-t border-slate-200 dark:border-slate-700 pt-6">
        {prev ? (
          <Link to={`/academy/${prev.id}`} className="flex items-center gap-2 text-sm font-ibm text-slate-500 hover:text-navy" data-testid="academy-prev-lesson">
            <ChevronLeft className="h-4 w-4" />{prev.title}
          </Link>
        ) : <span />}
        {next && (
          <Link to={`/academy/${next.id}`} className="flex items-center gap-2 text-sm font-ibm text-slate-500 hover:text-navy text-right" data-testid="academy-next-lesson">
            {next.title}<ChevronRight className="h-4 w-4" />
          </Link>
        )}
      </nav>
    </div>
  );
}
